import { useState } from "react";
import type { OwnerMissionView } from "../api";
import { statusLabel } from "../domain/statusCopy";
import { toneBorderClass, toneTextClass } from "./queueStateStyling";

/**
 * OWNEXP Slice 9 — Mission Composer Post-Submit UX.
 *
 * Nach dem Absenden gibt es GENAU zwei ehrliche Ergebnisse:
 *   MISSION ADMITTED            — Queue-Item, Zustand, Wartegrund, nächster
 *                                 Schritt, Links zu Mission/Queue.
 *   MISSION COULD NOT BE ADMITTED — exakter Owner-Grund, technische Ursache
 *                                 nur im Drilldown, was als Nächstes
 *                                 automatisch passiert, ob der Owner nötig ist.
 * Kein Chat-Handoff-Masquerading: eine nicht admittierte Mission wird nie als
 * Konversation oder (bedingt) abgeschlossen dargestellt.
 */

export type AdmissionErgebnis =
  | { ok: true; mission: OwnerMissionView; queue: { workItemId: string; missionId: string; queueState: string }; waitReason: string | null }
  | { ok: false; error: string; reason?: string };

const WARTEGRUND_TEXT: Record<string, string> = {
  lease_scope_busy: "Wartet auf die freie Schreibberechtigung (Writer-Lease) am Zielbereich — ein anderer Writer arbeitet dort noch. Sobald sie frei wird, startet die Mission automatisch.",
  lease_failed: "Die exklusive Schreibberechtigung konnte noch nicht geholt werden — die Mission startet automatisch, sobald die Zuteilung möglich ist. Es läuft nichts ungeschützt.",
};

function wartegrundText(waitReason: string | null): string | null {
  if (!waitReason) return null;
  for (const [schluessel, text] of Object.entries(WARTEGRUND_TEXT)) {
    if (waitReason.startsWith(schluessel)) return text;
  }
  return `Wartet automatisch fort: ${waitReason}`;
}

const FEHLER_URSACHE: Record<string, { grund: string; automatisch: string; ownerNoetig: string }> = {
  invalid_goal: {
    grund: "Das Ziel der Mission fehlt oder ist leer — ohne Ziel kann SIMO nichts aufnehmen.",
    automatisch: "Es wurde kein Auftrag erstellt; nichts läuft.",
    ownerNoetig: "Ja — bitte ein klares Ziel formulieren und erneut absenden.",
  },
  intake_failed: {
    grund: "SIMO konnte die Mission nicht in die Arbeits-Queue aufnehmen. Das ist eine Aufnahme-Störung, kein Problem deines Auftrags.",
    automatisch: "Es wurde kein Auftrag erstellt; nichts läuft. Ein erneuter Versuch ist jederzeit möglich.",
    ownerNoetig: "Nein — erneut absenden genügt. Wiederholt sich der Fehler, ist es eine Systemstörung (Operations Center prüfen).",
  },
};

export function MissionAdmissionNotice({
  ergebnis,
  onClose,
  onOpenMission,
  onOpenQueue,
}: {
  ergebnis: AdmissionErgebnis;
  onClose: () => void;
  onOpenMission: (missionId: string) => void;
  onOpenQueue: () => void;
}): JSX.Element {
  const [details, setDetails] = useState(false);

  if (ergebnis.ok) {
    const titel = ergebnis.mission.goal.split("\n")[0] ?? ergebnis.mission.goal;
    const wartet = wartegrundText(ergebnis.waitReason);
    const laeuft = ergebnis.queue.queueState === "RUNNING";
    return (
      <div
        className="fixed inset-x-0 bottom-0 z-50 flex justify-center px-3 pb-3 sm:pb-5"
        role="status"
        aria-live="polite"
        data-testid="mission-admission-notice"
      >
        <div className={`w-full max-w-2xl rounded-2xl border bg-jarvis-surface p-4 shadow-[0_24px_70px_rgba(0,0,0,0.5)] ${toneBorderClass("success")}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className={`text-xs font-semibold uppercase tracking-[0.16em] ${toneTextClass("success")}`}>Mission aufgenommen</p>
              <h3 className="mt-1 truncate text-sm font-semibold text-jarvis-text" title={titel}>
                {titel}
              </h3>
            </div>
            <button type="button" className="btn shrink-0 px-3 py-1.5 text-xs" onClick={onClose} aria-label="Meldung schließen">
              OK
            </button>
          </div>
          <dl className="mt-3 grid grid-cols-1 gap-x-4 gap-y-1.5 text-xs sm:grid-cols-2" data-testid="mission-admission-facts">
            <Feld label="Zustand" wert={`${statusLabel(ergebnis.queue.queueState) ?? ergebnis.queue.queueState}${laeuft ? " — läuft jetzt" : ""}`} />
            <Feld label="Wartegrund" wert={wartet ?? "Kein Wartegrund — Mission ist aufgenommen."} />
            <Feld label="Nächster Schritt" wert={naechsterSchritt(ergebnis)} />
            <Feld label="Mission (technisch)" wert={ergebnis.queue.missionId} mono />
          </dl>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="btn btn-primary px-3 py-1.5 text-xs"
              onClick={() => onOpenMission(ergebnis.queue.missionId)}
              data-testid="admission-open-mission"
            >
              Mission öffnen
            </button>
            <button
              type="button"
              className="btn px-3 py-1.5 text-xs"
              onClick={() => {
                onOpenQueue();
                onClose();
              }}
              data-testid="admission-open-queue"
            >
              Queue öffnen
            </button>
            <span className="ml-auto font-mono text-[10px] text-jarvis-muted" title="Queue-Item">
              Item {ergebnis.queue.workItemId}
            </span>
          </div>
        </div>
      </div>
    );
  }

  // ── MISSION COULD NOT BE ADMITTED ──
  // Server-Format: reason = "mission_could_not_be_admitted:<intake-grund>:…".
  const reasonBereinigt = (ergebnis.reason ?? "").replace(/^mission_could_not_be_admitted:/, "");
  const schluessel = reasonBereinigt.split(":")[0] ?? "";
  const vorlage = FEHLER_URSACHE[schluessel] ?? {
    grund: "SIMO hat die Mission abgelehnt — sie wurde nicht aufgenommen.",
    automatisch: "Es wurde kein Auftrag erstellt; nichts läuft.",
    ownerNoetig: "Erneut versuchen; wiederholtes Scheitern ist eine Systemstörung (Operations Center prüfen).",
  };
  return (
    <div
      className="fixed inset-x-0 bottom-0 z-50 flex justify-center px-3 pb-3 sm:pb-5"
      role="alert"
      data-testid="mission-admission-failure"
    >
      <div className={`w-full max-w-2xl rounded-2xl border bg-jarvis-surface p-4 shadow-[0_24px_70px_rgba(0,0,0,0.5)] ${toneBorderClass("critical")}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className={`text-xs font-semibold uppercase tracking-[0.16em] ${toneTextClass("critical")}`}>Mission konnte nicht aufgenommen werden</p>
            <h3 className="mt-1 text-sm font-semibold leading-snug text-jarvis-text">{vorlage.grund}</h3>
          </div>
          <button type="button" className="btn shrink-0 px-3 py-1.5 text-xs" onClick={onClose} aria-label="Meldung schließen">
            OK
          </button>
        </div>
        <div className="mt-2.5 grid gap-1 text-xs text-jarvis-muted">
          <p>
            <strong className="text-jarvis-text">Was passiert jetzt automatisch:</strong> {vorlage.automatisch}
          </p>
          <p>
            <strong className="text-jarvis-text">Braucht es dich?</strong> {vorlage.ownerNoetig}
          </p>
        </div>
        <button type="button" className="btn mt-2 px-2 py-1 text-[11px] text-jarvis-muted underline underline-offset-2" onClick={() => setDetails(!details)} aria-expanded={details}>
          {details ? "Technische Ursache ausblenden" : "Technische Ursache einblenden"}
        </button>
        {details && (
          <p className="mt-1 break-words rounded-md border border-white/10 bg-black/25 px-2 py-1.5 font-mono text-[10px] text-jarvis-muted" data-testid="admission-failure-technical">
            error={ergebnis.error} · reason={ergebnis.reason ?? "unbekannt"}
          </p>
        )}
      </div>
    </div>
  );
}

function naechsterSchritt(ergebnis: Extract<AdmissionErgebnis, { ok: true }>): string {
  if (ergebnis.queue.queueState === "RUNNING") return "Läuft jetzt — Fortschritt und Ergebnis im Missions-Detail verfolgen.";
  if (ergebnis.waitReason) return "Startet automatisch, sobald die Schreibberechtigung frei ist — kein Handeln nötig.";
  return "Der Supervisor nimmt die Mission im nächsten Takt auf und startet sie automatisch.";
}

function Feld({ label, wert, mono }: { label: string; wert: string; mono?: boolean }): JSX.Element {
  return (
    <div className="min-w-0">
      <dt className="text-[10px] uppercase tracking-wider text-jarvis-muted">{label}</dt>
      <dd className={`truncate text-jarvis-text ${mono ? "font-mono text-[10px]" : ""}`} title={wert}>
        {wert}
      </dd>
    </div>
  );
}
